/**
 * Tutorial guidato dell'Autenticazione social del sito pubblico.
 *
 * I preparativi sono le righe dei fornitori, che vanno aperte per mostrare i campi
 * di configurazione: il tutorial apre Google per il giro comune e Apple per il passo
 * sui suoi campi in piu'. `stopAuthenticationTour` richiude quello che ha aperto lui,
 * e solo quello: una riga gia' aperta dall'utente resta com'era.
 *
 * Nessun passo tocca gli interruttori: quelli salvano subito, e un tutorial non deve
 * accendere o spegnere un accesso vero.
 */
const SCOPE = '[data-tour-scope="ecommerce-authentication"]';

const at = name => `${SCOPE} [data-tour="${name}"]`;

/** Righe aperte dal tutorial, da richiudere a fine giro. */
const opened = new Set();

const visible = selector => {
	const element = document.querySelector(selector);

	return !!element && element.getClientRects().length > 0;
};

/** Apre la riga di un fornitore, se non e' gia' aperta. */
function open(name, probe) {
	if (visible(probe)) return;

	const toggle = document.querySelector(at(`expand-${name}`));

	if (toggle) {
		opened.add(name);
		toggle.click();
	}
}

/** Richiude la riga di un fornitore, ma solo se a spalancarla e' stato il tutorial. */
function close(name, probe) {
	if (!opened.has(name)) return;

	opened.delete(name);

	if (visible(probe)) {
		document.querySelector(at(`expand-${name}`))?.click();
	}
}

const openGoogle = () => {
	close('apple', at('apple-key'));
	open('google', at('callback'));
};

const openApple = () => {
	close('google', at('callback'));
	open('apple', at('apple-key'));
};

export function stopAuthenticationTour() {
	close('google', at('callback'));
	close('apple', at('apple-key'));
}

export function createAuthenticationTourSteps() {
	return [
		{
			body: "Da qui abiliti l'accesso al sito con un account che il cliente ha gia': Google, Facebook, Amazon o Apple. Chi entra cosi' si ritrova registrato senza compilare il modulo, quindi arriva senza i dati che il form chiede di solito.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'Accesso con account social',
		},
		{
			body: 'Un fornitore per riga, sempre gli stessi quattro: non se ne aggiungono, si accendono e si configurano quelli che ci sono.',
			key: 'table',
			placement: 'top',
			target: at('table'),
			title: "L'elenco dei fornitori",
		},
		{
			body: "L'interruttore accende e spegne il fornitore sul sito, e salva subito: appena lo tocchi il pulsante compare o sparisce dalla pagina di accesso. Accendilo solo dopo aver inserito le credenziali, altrimenti chi ci prova trova un errore.",
			key: 'switch',
			placement: 'left',
			target: at('switch'),
			title: 'Accendere il fornitore',
		},
		{
			body: "Il piu' a inizio riga apre la configurazione del fornitore sotto la riga stessa. Premilo di nuovo per richiuderla.",
			before: stopAuthenticationTour,
			key: 'expand',
			placement: 'right',
			target: at('expand-google'),
			timeout: 400,
			title: 'Aprire la configurazione',
		},
		{
			body: "Questo e' l'indirizzo a cui il fornitore rimanda dopo il consenso: va copiato di la', nella console del fornitore, fra gli URI di reindirizzamento consentiti: il pulsante Copia lo mette negli appunti e Apri la console porta alla pagina giusta. Se manca o e' scritto diverso anche di una barra, l'accesso si ferma con un errore di redirect.",
			before: openGoogle,
			key: 'callback',
			placement: 'top',
			target: at('callback'),
			timeout: 400,
			title: "L'indirizzo di ritorno",
		},
		{
			body: "Client ID e Client Secret si generano nella console del fornitore, dove hai registrato l'applicazione, e si incollano qui. Il segreto vale come una password: resta nascosto finche' non premi l'occhio, e si rigenera di la' se pensi sia in giro.",
			before: openGoogle,
			key: 'credentials',
			placement: 'top',
			target: at('credentials'),
			timeout: 400,
			title: 'Le credenziali',
		},
		{
			body: 'Salva registra le credenziali del fornitore. Ogni riquadro ha il suo pulsante e salva solo il proprio fornitore; nella riga, il chip dice se mancano ancora dei campi.',
			before: openGoogle,
			key: 'confirm',
			placement: 'right',
			target: at('confirm'),
			timeout: 400,
			title: 'Salvare la configurazione',
		},
		{
			body: "Apple chiede qualcosa in piu' degli altri: come Client ID vuole il Services ID (del tipo com.tuaazienda.web), poi Team ID e Key ID, e infine il contenuto del file .p8 scaricato da Apple Developer, da incollare per intero in questo riquadro. Il file si scarica una volta sola: se lo perdi va creata una chiave nuova.",
			before: openApple,
			key: 'apple',
			missing: 'skip',
			placement: 'top',
			target: at('apple-key'),
			timeout: 600,
			title: 'Il caso di Apple',
		},
	];
}
